import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  useTheme,
  LinearProgress,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { toast } from "react-toastify";
import axios from "axios";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import VideocamIcon from "@mui/icons-material/Videocam";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const CLOUDINARY_CLOUD = "dcmr4gnq5";
const CLOUDINARY_IMAGE_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`;
const CLOUDINARY_VIDEO_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/video/upload`;
const IMAGE_PRESET = "book_uploads";
const VIDEO_PRESET = "book_digital_files";
const PLAYABLE_VIDEO_MIME_TYPES = ["video/mp4", "video/webm", "video/ogg"];
const PLAYABLE_VIDEO_EXTENSIONS = [".mp4", ".webm", ".ogv", ".ogg"];

const AddGalleryItem = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isLight = theme.palette.mode === "light";

  const [mediaFile, setMediaFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("events");
  const [type, setType] = useState("photo");
  const [uploading, setUploading] = useState(false);

  const adminToken = localStorage.getItem("adminToken");

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isVideoFile = file.type.startsWith("video/");
    if (isVideoFile) {
      const lowerName = file.name.toLowerCase();
      const hasPlayableVideoExt = PLAYABLE_VIDEO_EXTENSIONS.some((ext) =>
        lowerName.endsWith(ext),
      );
      if (
        !PLAYABLE_VIDEO_MIME_TYPES.includes(file.type) &&
        !hasPlayableVideoExt
      ) {
        toast.error("Use MP4, WebM, or OGG video for frontend playback");
        e.target.value = "";
        return;
      }
    }

    if (file.size > 100 * 1024 * 1024) {
      toast.error("File must be under 100MB");
      e.target.value = "";
      return;
    }
    setMediaFile(file);
    setType(file.type.startsWith("video/") ? "video" : "photo");
  };

  const uploadToCloudinary = async (file, isVideo) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", isVideo ? VIDEO_PRESET : IMAGE_PRESET);
    formData.append("cloud_name", CLOUDINARY_CLOUD);

    const url = isVideo ? CLOUDINARY_VIDEO_URL : CLOUDINARY_IMAGE_URL;
    const res = await fetch(url, { method: "POST", body: formData });
    if (!res.ok) throw new Error(`Cloudinary upload failed: ${res.status}`);
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    return data.secure_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploading) return;
    if (!mediaFile) return toast.error("Please select a media file");
    if (!title.trim()) return toast.error("Please enter a title");

    try {
      setUploading(true);
      const isVideo = mediaFile.type.startsWith("video/");
      const resolvedType = isVideo ? "video" : "photo";

      toast.info(
        isVideo
          ? "Uploading video to Cloudinary..."
          : "Uploading image to Cloudinary...",
      );
      const mediaUrl = await uploadToCloudinary(mediaFile, isVideo);

      toast.info("Saving to gallery...");
      const { data } = await axios.post(
        BASE_URL + "/api/admin/add-gallery-item",
        { title: title.trim(), category, type: resolvedType, mediaUrl },
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (data.success) {
        toast.success(data.message || "Gallery item added!");
        setMediaFile(null);
        setTitle("");
        setCategory("events");
        setType("photo");
        document.getElementById("media-file-input").value = "";
      } else {
        toast.error(data.message || "Failed to add gallery item");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "An error occurred",
      );
    } finally {
      setUploading(false);
    }
  };

  const inputBg = isLight ? "#ffffff" : colors.primary[500];
  const inputText = isLight ? "#111827" : colors.grey[100];
  const inputLabel = isLight ? "#4b5563" : colors.grey[300];
  const inputBorder = isLight ? "#d1d5db" : colors.primary[300];
  const cardBg = isLight ? "#f9fafb" : colors.primary[400];
  const cardBorder = isLight ? "#e5e7eb" : colors.primary[300];

  const cardSx = {
    backgroundColor: cardBg,
    borderRadius: "16px",
    padding: "28px",
    border: `1px solid ${cardBorder}`,
  };

  const sectionLabelSx = {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: colors.greenAccent[isLight ? 500 : 400],
    mb: 3,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: inputBg,
      "& fieldset": { borderColor: inputBorder },
      "&:hover fieldset": { borderColor: colors.greenAccent[400] },
      "&.Mui-focused fieldset": { borderColor: colors.greenAccent[400] },
    },
    "& .MuiInputLabel-root": { color: inputLabel },
    "& .MuiInputLabel-root.Mui-focused": {
      color: colors.greenAccent[isLight ? 500 : 400],
    },
    "& .MuiInputBase-input": { color: inputText },
    "& .MuiSelect-icon": { color: inputLabel },
  };

  const isVideo = mediaFile?.type?.startsWith("video/");

  return (
    <Box m="20px">
      <Header
        title="ADD GALLERY ITEM"
        subtitle="Upload a photo or video to your gallery"
      />
      {uploading && (
        <LinearProgress
          sx={{
            mb: 2,
            borderRadius: 2,
            "& .MuiLinearProgress-bar": {
              backgroundColor: colors.greenAccent[400],
            },
            backgroundColor: isLight ? "#d1fae5" : colors.primary[300],
          }}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box
          display="grid"
          gridTemplateColumns="300px 1fr"
          gap="24px"
          alignItems="start"
        >
          {/* LEFT — Media Upload */}
          <Box sx={cardSx}>
            <Typography sx={sectionLabelSx}>
              <PhotoLibraryIcon sx={{ fontSize: 16 }} />
              Media File
            </Typography>

            <label
              htmlFor="media-file-input"
              style={{ cursor: uploading ? "not-allowed" : "pointer" }}
            >
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "1/1",
                  borderRadius: "12px",
                  border: `2px dashed ${mediaFile ? colors.greenAccent[400] : isLight ? "#d1d5db" : colors.primary[300]}`,
                  backgroundColor: isLight ? "#ffffff" : colors.primary[500],
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: colors.greenAccent[400],
                    backgroundColor: isLight ? "#f0fdf4" : colors.primary[400],
                  },
                }}
              >
                {mediaFile && !isVideo ? (
                  <img
                    src={URL.createObjectURL(mediaFile)}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : mediaFile && isVideo ? (
                  <Box textAlign="center" p={3}>
                    <Avatar
                      sx={{
                        bgcolor: isLight ? "#dbeafe" : colors.blueAccent[700],
                        width: 64,
                        height: 64,
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <VideocamIcon
                        sx={{
                          color: isLight ? "#2563eb" : colors.blueAccent[300],
                          fontSize: 32,
                        }}
                      />
                    </Avatar>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color={isLight ? "#374151" : colors.grey[200]}
                    >
                      {mediaFile.name}
                    </Typography>
                    <Typography
                      fontSize="11px"
                      color={isLight ? "#9ca3af" : colors.grey[400]}
                      mt={0.5}
                    >
                      {(mediaFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                ) : (
                  <Box textAlign="center" p={3}>
                    <Avatar
                      sx={{
                        bgcolor: isLight ? "#d1fae5" : colors.greenAccent[700],
                        width: 56,
                        height: 56,
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <CloudUploadIcon
                        sx={{
                          color: isLight
                            ? colors.greenAccent[600]
                            : colors.greenAccent[300],
                        }}
                      />
                    </Avatar>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color={isLight ? "#374151" : colors.grey[200]}
                    >
                      Click to upload
                    </Typography>
                    <Typography
                      fontSize="11px"
                      color={isLight ? "#9ca3af" : colors.grey[400]}
                      mt={0.5}
                    >
                      Images or MP4/WebM/OGG videos · Max 100MB
                    </Typography>
                  </Box>
                )}
              </Box>
            </label>
            <input
              id="media-file-input"
              type="file"
              accept="image/*,video/mp4,video/webm,video/ogg"
              onChange={handleMediaChange}
              disabled={uploading}
              hidden
            />

            {mediaFile && (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mt={2}
                p={1.5}
                sx={{
                  backgroundColor: isLight ? "#f0fdf4" : colors.primary[500],
                  borderRadius: "8px",
                  border: `1px solid ${isLight ? "#86efac" : colors.greenAccent[700]}`,
                }}
              >
                <CheckCircleIcon
                  sx={{
                    color: isLight ? "#16a34a" : colors.greenAccent[400],
                    fontSize: 16,
                  }}
                />
                <Typography
                  fontSize="11px"
                  color={isLight ? "#166534" : colors.greenAccent[300]}
                  noWrap
                >
                  {mediaFile.name}
                </Typography>
              </Box>
            )}
          </Box>

          {/* RIGHT — Fields */}
          <Box display="flex" flexDirection="column" gap="20px">
            <Box sx={cardSx}>
              <Typography sx={sectionLabelSx}>
                <PhotoLibraryIcon sx={{ fontSize: 16 }} />
                Gallery Details
              </Typography>
              <Box display="flex" flexDirection="column" gap="16px">
                <TextField
                  label="Title *"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  sx={inputSx}
                  disabled={uploading}
                  required
                  placeholder="e.g. Book Launch Nairobi 2024"
                />
                <TextField
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  select
                  fullWidth
                  sx={inputSx}
                  disabled={uploading}
                >
                  {[
                    { value: "events", label: "Events" },
                    { value: "retreats", label: "Retreats" },
                    { value: "books", label: "Book Signings" },
                    { value: "community", label: "Community" },
                  ].map((c) => (
                    <MenuItem
                      key={c.value}
                      value={c.value}
                      sx={{ color: inputText }}
                    >
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Box>
                  <Typography
                    fontSize="11px"
                    fontWeight={700}
                    letterSpacing="0.1em"
                    textTransform="uppercase"
                    color={inputLabel}
                    mb={1.5}
                  >
                    Media Type
                  </Typography>
                  <ToggleButtonGroup
                    value={type}
                    exclusive
                    onChange={(_, val) => val && setType(val)}
                    disabled={uploading}
                    sx={{
                      "& .MuiToggleButton-root": {
                        color: isLight ? "#374151" : colors.grey[300],
                        borderColor: isLight ? "#d1d5db" : colors.primary[300],
                        borderRadius: "8px !important",
                        px: 3,
                        py: 1,
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "none",
                        mx: 0.5,
                        gap: 1,
                        "&.Mui-selected": {
                          backgroundColor:
                            colors.greenAccent[isLight ? 600 : 700],
                          color: isLight ? "#ffffff" : colors.greenAccent[100],
                          borderColor: colors.greenAccent[500],
                          "&:hover": {
                            backgroundColor: colors.greenAccent[500],
                          },
                        },
                        "&:hover": {
                          backgroundColor: isLight
                            ? "#f0fdf4"
                            : colors.primary[300],
                        },
                      },
                    }}
                  >
                    <ToggleButton value="photo">
                      <PhotoCameraIcon sx={{ fontSize: 16 }} /> Photo
                    </ToggleButton>
                    <ToggleButton value="video">
                      <VideocamIcon sx={{ fontSize: 16 }} /> Video
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            </Box>

            <Button
              type="submit"
              variant="contained"
              disabled={uploading}
              startIcon={uploading ? null : <AddIcon />}
              fullWidth
              sx={{
                backgroundColor: colors.greenAccent[isLight ? 500 : 600],
                color: isLight ? "#ffffff" : colors.primary[900],
                fontWeight: 700,
                fontSize: "14px",
                py: 1.8,
                borderRadius: "12px",
                textTransform: "none",
                letterSpacing: "0.05em",
                "&:hover": {
                  backgroundColor: colors.greenAccent[isLight ? 400 : 500],
                  boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                },
                "&:disabled": {
                  backgroundColor: isLight ? "#d1d5db" : colors.primary[300],
                  color: isLight ? "#9ca3af" : colors.grey[500],
                },
              }}
            >
              {uploading ? "Adding to Gallery..." : "Add to Gallery"}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default AddGalleryItem;
